import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Button from 'reactstrap/lib/Button';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { Link } from 'site-modules/shared/components/link/link';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import {
  LinkDataPropType,
  BUTTONS_DEFAULT_TITLE,
  VIDEO_CARD_THUMBNAIL_SIZE,
} from 'site-modules/shared/constants/top-rated/top-rated';
import { VideoPreview } from 'site-modules/shared/components/video-preview/video-preview';

const BUTTON_CLASS_NAME = 'card-btn w-100 px-1 px-lg-0_5 px-xl-1 py-0_25 mb-1 size-16';
const VIDEO_CREATIVE_ID = 'card-video';

export function VehicleCard({
  videoId,
  videoTitle,
  videoAspectRatio = '16:9',
  vehicleContent,
  vehicleLinkData,
  primaryButtonData,
  secondaryButtonData,
  videoTranscript,
  videoThumbnailUrl,
  openVideoModal,
  isMobile = false,
}) {
  const transcriptScrollTopId = `scrollTop-transcript-${videoId}`;

  return (
    <Fragment>
      <Row className="vehicle-card card flex-row mx-0">
        <Col xs={12} lg={6} className="px-0 pe-lg-0_25">
          <VideoPreview
            videoId={videoId}
            videoTitle={videoTitle}
            videoAspectRatio={videoAspectRatio}
            videoThumbnailUrl={videoThumbnailUrl ? getFullImageUrl(videoThumbnailUrl, VIDEO_CARD_THUMBNAIL_SIZE) : null}
            openVideoModal={openVideoModal}
            creativeId={VIDEO_CREATIVE_ID}
            isStaticPos={isMobile}
          />
        </Col>
        <Col xs={12} lg={6} className="card-content pt-1 px-0_5 px-md-1 ps-lg-0_75">
          <Link
            className="text-primary-darker d-block mb-0_5 px-0_5 px-md-0"
            to={vehicleLinkData.url}
            target={vehicleLinkData.blank ? '_blank' : null}
            rel={vehicleLinkData.nofollow ? 'nofollow' : null}
            data-tracking-id="view_content"
          >
            <h3 className="size-20 mb-0">{vehicleLinkData.title}</h3>
          </Link>
          <ContentFragment classes="text-gray-darker mb-1 px-0_5 px-md-0 size-16">{vehicleContent}</ContentFragment>
          <Row>
            {secondaryButtonData.url && (
              <Col xs={6} md="auto" className="col-md">
                <Button
                  tag={Link}
                  to={secondaryButtonData.url}
                  color="outline-primary-b"
                  className={BUTTON_CLASS_NAME}
                  target={secondaryButtonData.blank ? '_blank' : null}
                  rel={secondaryButtonData.nofollow ? 'nofollow' : null}
                  data-tracking-id="type_select"
                >
                  {secondaryButtonData.title || BUTTONS_DEFAULT_TITLE.SECONDARY}
                </Button>
              </Col>
            )}
            {primaryButtonData.url && (
              <Col xs={6} md="auto" className="col-md">
                <Button
                  tag={Link}
                  to={primaryButtonData.url}
                  color="primary-b"
                  className={BUTTON_CLASS_NAME}
                  target={primaryButtonData.blank ? '_blank' : null}
                  rel={primaryButtonData.nofollow ? 'nofollow' : null}
                  data-tracking-id="view_content"
                >
                  {primaryButtonData.title || BUTTONS_DEFAULT_TITLE.PRIMARY}
                </Button>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
      {videoTranscript && (
        <ContentCollapse
          textClosed="See transcript"
          textOpen="Close transcript"
          justify="justify-content-start"
          btnClasses="me-0_25 p-0"
          btnContainerClasses="transcript-button"
          btnTextClasses="fw-normal medium"
          btnArrowClasses="size-10 text-primary-darker"
          classes="transcript mt-0_5 px-0_5"
          showBtnBelow
          isEclipseFade={false}
          resetParams={{
            offset: SCROLL_OFFSET,
            duration: SCROLL_DURATION,
            to: transcriptScrollTopId,
          }}
        >
          <ScrollElement id={transcriptScrollTopId} />
          <div className="py-0_25 text-gray-darker">{videoTranscript}</div>
        </ContentCollapse>
      )}
    </Fragment>
  );
}

VehicleCard.propTypes = {
  openVideoModal: PropTypes.func.isRequired,
  videoId: PropTypes.string.isRequired,
  videoTitle: PropTypes.string.isRequired,
  vehicleContent: PropTypes.string.isRequired,
  videoTranscript: PropTypes.string.isRequired,
  videoThumbnailUrl: PropTypes.string.isRequired,
  vehicleLinkData: LinkDataPropType.isRequired,
  primaryButtonData: LinkDataPropType.isRequired,
  secondaryButtonData: LinkDataPropType.isRequired,
  videoAspectRatio: PropTypes.string,
  isMobile: PropTypes.bool,
};
